import { DEFAULT_CURRENCY, formatMoney, toMajorUnits, toMinorUnits } from "@vouch/shared";
import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Button, Card, Input, Modal, Select, Spinner } from "../components/ui";
import {
  useAccountHistory,
  useCreateRecurring,
  useDeleteRecurring,
  useForecast,
  useRecurring,
} from "../lib/hooks";

const RANGES = [3, 6, 12];
const LINE_COLORS = ["#2563eb", "#0ea5e9", "#14b8a6", "#f59e0b", "#ec4899", "#8b5cf6"];

export function ForecastPage(): JSX.Element {
  const [months, setMonths] = useState(6);
  const { data: forecast, isLoading } = useForecast(months);
  const { data: history } = useAccountHistory(6);
  const { data: rules = [] } = useRecurring();
  const createRule = useCreateRecurring();
  const deleteRule = useDeleteRecurring();
  const [open, setOpen] = useState(false);

  const currency = DEFAULT_CURRENCY;

  // Past (actual, per-account + total) then future (forecast total) on one axis.
  const histRows = (history?.points ?? []).map((p) => {
    const row: Record<string, number | string> = { month: String(p.month) };
    for (const [k, v] of Object.entries(p)) if (k !== "month") row[k] = toMajorUnits(v as number);
    return row;
  });
  const futureRows = (forecast ?? []).slice(1).map((m) => ({
    month: m.month,
    total: toMajorUnits(m.closingBalance),
  }));
  const chartRows = [...histRows, ...futureRows];
  const firstFuture = futureRows[0]?.month;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-brand-500">Forecast</h1>
        <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
          {RANGES.map((r) => (
            <button
              key={r}
              onClick={() => setMonths(r)}
              className={`rounded-md px-3 py-1 text-sm font-medium ${
                months === r ? "bg-white text-brand-500 shadow-sm" : "text-slate-500"
              }`}
            >
              {r}m
            </button>
          ))}
        </div>
      </div>

      <Card>
        {isLoading ? (
          <Spinner />
        ) : forecast && forecast.length > 0 ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-500">
              Projected balance by <strong>{forecast.at(-1)?.month}</strong>:{" "}
              <span className="font-semibold text-brand-500">
                {formatMoney(forecast.at(-1)?.closingBalance ?? 0, currency)}
              </span>
            </p>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={chartRows}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} width={48} />
                <Tooltip formatter={(v: number) => formatMoney(Math.round(v * 100), currency)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {firstFuture && (
                  <ReferenceLine x={firstFuture} stroke="#94a3b8" strokeDasharray="4 4" label={{ value: "forecast", fontSize: 10, fill: "#94a3b8" }} />
                )}
                {(history?.accounts ?? []).map((name, i) => (
                  <Line
                    key={name}
                    type="monotone"
                    dataKey={name}
                    stroke={LINE_COLORS[i % LINE_COLORS.length]}
                    strokeWidth={1.5}
                    dot={false}
                    connectNulls={false}
                  />
                ))}
                <Line type="monotone" dataKey="total" stroke="#1a2b48" strokeWidth={3} dot={{ r: 2 }} />
              </LineChart>
            </ResponsiveContainer>
            <p className="text-xs text-slate-400">
              Solid lines are actual balances; the bold line continues past the dashed marker as
              the projected total.
            </p>
          </div>
        ) : (
          <p className="text-sm text-slate-500">No forecast data yet.</p>
        )}
      </Card>

      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-700">Recurring income & expenses</h2>
          <Button variant="ghost" onClick={() => setOpen(true)}>
            <Plus size={16} /> Add
          </Button>
        </div>
        <Card>
          {rules.length > 0 ? (
            <ul className="divide-y divide-slate-100">
              {rules.map((r) => (
                <li key={r.id} className="flex items-center justify-between py-2.5">
                  <div>
                    <p className="text-sm font-medium text-slate-700">
                      {r.description || r.kind}
                    </p>
                    <p className="text-xs text-slate-400">
                      {r.cadence} · {r.kind}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={
                        r.kind === "income"
                          ? "text-sm font-semibold text-green-600"
                          : "text-sm font-semibold text-red-600"
                      }
                    >
                      {formatMoney(r.amount, currency)}
                    </span>
                    <button
                      onClick={() => deleteRule.mutate(r.id)}
                      className="text-slate-300 hover:text-red-500"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500">
              Add recurring items (salary, rent, subscriptions) to power the forecast.
            </p>
          )}
        </Card>
      </section>

      {open && (
        <AddRecurringModal
          busy={createRule.isPending}
          onClose={() => setOpen(false)}
          onSubmit={async (vals) => {
            await createRule.mutateAsync(vals);
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}

function AddRecurringModal({
  busy,
  onClose,
  onSubmit,
}: {
  busy: boolean;
  onClose: () => void;
  onSubmit: (vals: {
    kind: "income" | "expense";
    amountMinor: number;
    cadence: "weekly" | "biweekly" | "monthly" | "quarterly" | "yearly";
    description?: string;
  }) => Promise<void>;
}): JSX.Element {
  const [kind, setKind] = useState<"income" | "expense">("expense");
  const [cadence, setCadence] = useState<
    "weekly" | "biweekly" | "monthly" | "quarterly" | "yearly"
  >("monthly");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");

  return (
    <Modal open title="Add recurring item" onClose={onClose}>
      <div className="space-y-3">
        <Select label="Kind" value={kind} onChange={(e) => setKind(e.target.value as "income" | "expense")}>
          <option value="income">Income</option>
          <option value="expense">Expense</option>
        </Select>
        <Select
          label="Cadence"
          value={cadence}
          onChange={(e) =>
            setCadence(e.target.value as "weekly" | "biweekly" | "monthly" | "quarterly" | "yearly")
          }
        >
          <option value="weekly">Weekly</option>
          <option value="biweekly">Biweekly</option>
          <option value="monthly">Monthly</option>
          <option value="quarterly">Quarterly</option>
          <option value="yearly">Yearly</option>
        </Select>
        <Input label="Amount" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        <Input label="Description" value={description} onChange={(e) => setDescription(e.target.value)} />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={busy}
            onClick={() => {
              const amountMinor = toMinorUnits(Number(amount));
              if (amountMinor > 0)
                void onSubmit({ kind, amountMinor, cadence, description: description || undefined });
            }}
          >
            {busy ? "Saving…" : "Add"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
